import React, { useCallback, useEffect, useRef, useState } from 'react';
import { VideoCameraIcon } from '@heroicons/react/24/solid';

const POSTER_SIZE = 320;

// onDuration receives only a finite decoder-reported duration (metadata, not
// readiness); onPreviewReady fires once, for the current src, when a decoded
// frame was actually accepted as the poster; onError reports bytes the browser
// could not decode. Callers end their Loading state only on ready or error.
const VideoThumbnail = ({ src, alt = 'Video preview', onDuration, onPreviewReady, onError }) => {
  const videoRef = useRef(null);
  const posterUrlRef = useRef(null);
  const targetTimeRef = useRef(0);
  const generationRef = useRef(0);
  const captureStartedRef = useRef(false);
  const [posterUrl, setPosterUrl] = useState(null);
  const readyRef = useRef(onPreviewReady);
  readyRef.current = onPreviewReady;

  useEffect(() => {
    generationRef.current += 1;
    captureStartedRef.current = false;
    targetTimeRef.current = 0;
    setPosterUrl(null);

    return () => {
      generationRef.current += 1;
      if (posterUrlRef.current) {
        URL.revokeObjectURL?.(posterUrlRef.current);
        posterUrlRef.current = null;
      }
    };
  }, [src]);

  const captureFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || captureStartedRef.current || !video.videoWidth || !video.videoHeight) return;

    captureStartedRef.current = true;
    const generation = generationRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = POSTER_SIZE;
    canvas.height = POSTER_SIZE;
    const context = canvas.getContext('2d');
    if (!context) return;

    const sourceWidth = video.videoWidth;
    const sourceHeight = video.videoHeight;
    const cropSize = Math.min(sourceWidth, sourceHeight);
    const sourceX = (sourceWidth - cropSize) / 2;
    const sourceY = (sourceHeight - cropSize) / 2;
    context.drawImage(
      video,
      sourceX,
      sourceY,
      cropSize,
      cropSize,
      0,
      0,
      POSTER_SIZE,
      POSTER_SIZE,
    );

    const acceptPoster = (blob) => {
      if (!blob || generation !== generationRef.current) return;
      const nextUrl = URL.createObjectURL(blob);
      if (generation !== generationRef.current) {
        URL.revokeObjectURL?.(nextUrl);
        return;
      }
      if (posterUrlRef.current) URL.revokeObjectURL?.(posterUrlRef.current);
      posterUrlRef.current = nextUrl;
      setPosterUrl(nextUrl);
      readyRef.current?.();
    };

    if (typeof canvas.toBlob === 'function') {
      canvas.toBlob(acceptPoster, 'image/jpeg', 0.82);
    } else {
      setPosterUrl(canvas.toDataURL('image/jpeg', 0.82));
      readyRef.current?.();
    }
  }, []);

  const handleLoadedMetadata = (event) => {
    const video = event.currentTarget;
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    if (duration > 0) onDuration?.(duration);
    const targetTime = duration > 0.2 ? Math.min(0.1, duration / 2) : 0;
    targetTimeRef.current = targetTime;
    if (targetTime > 0) {
      video.currentTime = targetTime;
    } else if (video.readyState >= 2) {
      captureFrame();
    }
  };

  const handleLoadedData = (event) => {
    if (Math.abs(event.currentTarget.currentTime - targetTimeRef.current) < 0.05) {
      captureFrame();
    }
  };

  return (
    <div className="video-preview-container">
      <video
        ref={videoRef}
        src={src}
        className="video-thumbnail-source"
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
        tabIndex={-1}
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={handleLoadedData}
        onSeeked={captureFrame}
        onError={onError}
      />
      {posterUrl ? (
        <img src={posterUrl} alt={alt} className="media-preview" />
      ) : (
        <div className="media-preview video-preview-loading" role="status" aria-label="Loading video preview" />
      )}
      <VideoCameraIcon className="video-icon" aria-label="Video" />
    </div>
  );
};

export { POSTER_SIZE };
export default VideoThumbnail;
